import { Close, Menu } from '@mui/icons-material';
import { useKeycloak } from '@react-keycloak/web';
import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

import { EUserRoles } from '../../enums/EUserRoles';
import { disableScroll } from '../../helpers/helper';
import { useAppDispatch, useAppSelector } from '../../hooks/hooks';
import { SvNotificationsService } from '../../services/SvNotificationServices';
import { updateDashboardDim, updateHeaderDim } from '../../store/appSlice';
import { userHasRole } from '../../utils/utils';
import CLocalizedTextStringStatic from '../CLocalizedTextStringStatic';
import ImageWrapper from '../ImageWrapper';
import MobileMenu from './MobileMenu';

interface State {
  isMenuExpanded?: boolean;
  hasNotifications?: boolean;
}

interface headerProps {
  mobileNavItems?: JSX.Element;
  headerFloatRightItems?: JSX.Element;
}

export default function Header({ mobileNavItems, headerFloatRightItems }: headerProps) {
  const location = useLocation();
  const effectiveWidthRef = useRef<HTMLDivElement>(null);
  const { keycloak } = useKeycloak();

  const dispatch = useAppDispatch();
  const [state, setState] = useState<State>({ hasNotifications: false });
  const updateState = (obj: Partial<State>) => {
    setState((state) => ({ ...state, ...obj }));
  };
  const { isMenuExpanded } = state;
  const headerDom = useRef<HTMLDivElement>(null);
  const { breadcrumbReplacementTextDict } = useAppSelector((state) => state.dashboard);
  const isAdmin = userHasRole(keycloak.tokenParsed, [[EUserRoles.ADMIN]]);
  const isStaff = userHasRole(keycloak.tokenParsed, [[EUserRoles.STAFF]]);
  const isPortfolioManager = userHasRole(keycloak.tokenParsed, [[EUserRoles.PORTFOLIO_MANAGER]]);
  const canAccess = isAdmin || isStaff || isPortfolioManager;

  useEffect(() => {
    if (!canAccess) {
      updateState({ hasNotifications: false });
      return;
    }

    (async () => {
      const res = await SvNotificationsService.getNotifications({
        page: 1,
        sorting: {
          sortCol: 0,
          ascending: 'descending'
        }
      });

      const data = await res.getData();

      updateState({
        hasNotifications: (data?.meta?.pagination?.total ?? 0) > 0
      });
    })();
  }, [location.key, canAccess]);

  const doLayoutCalculations = () => {
    updateState({ isMenuExpanded: false });
    dispatch(
      updateHeaderDim({
        h: headerDom.current?.getBoundingClientRect().height ?? 0,
        w: effectiveWidthRef.current?.getBoundingClientRect().width ?? 0
      })
    );
    dispatch(updateDashboardDim({ h: window.innerHeight - (headerDom.current?.getBoundingClientRect().height ?? 0) }));
  };

  useEffect(() => {
    const slug = location.pathname.split('/').pop();
    const index = Object.keys(breadcrumbReplacementTextDict).findIndex((e) => e === slug);
    const label = Object.values(breadcrumbReplacementTextDict)[index];
    if (!label) return;
    document.title = `${label} - neXDos`;
  }, [location.pathname, breadcrumbReplacementTextDict]);

  useEffect(() => {
    disableScroll(isMenuExpanded ?? false);
  }, [isMenuExpanded]);

  useEffect(() => {
    doLayoutCalculations();
    window.addEventListener('resize', doLayoutCalculations);
    return () => {
      window.removeEventListener('resize', doLayoutCalculations);
    };
  }, []);

  const clientPortalLabel = CLocalizedTextStringStatic('header.clientPortal');

  return (
    <>
      <div className={'w-full'} ref={effectiveWidthRef} />
      <div
        className={
          'fixed top-0 z-50 flex gap-0 md:gap-4 border-b bg-white items-center before:bg-white before:w-full before:h-14 before:absolute before:-z-10'
        }
        id={'header'}
        ref={headerDom}
        style={{ width: effectiveWidthRef.current?.getBoundingClientRect().width ?? 0 }}
      >
        {(headerFloatRightItems != null || mobileNavItems != null) && (
          <div className={'button md:hidden w-20 pl-1'}>
            <div
              className={'aspect-square flex items-center justify-center h-12 w-12 cursor-pointer'}
              style={{ fontSize: '1.5rem', padding: '0.8rem' }}
              onClick={() => {
                updateState({ isMenuExpanded: !isMenuExpanded });
              }}
            >
              <span className="relative pointer-events-none -mt-1">
                {!isMenuExpanded ? <Menu /> : <Close />}

                {state.hasNotifications && !state.isMenuExpanded && (
                  <span className="absolute top-1 w-2 h-2 bg-red-500 rounded-full" />
                )}
              </span>
              <MobileMenu
                isMenuExpanded={isMenuExpanded ?? false}
                onClose={() => {
                  updateState({ isMenuExpanded: false });
                }}
                mobileNavItems={mobileNavItems}
                hasNotifications={state.hasNotifications}
              />
            </div>
          </div>
        )}
        <Link
          to={'/'}
          className={
            'flex py-3 justify-center md:justify-start px-4 gap-2 sm:gap-3 items-end select-none hover:opacity-70'
          }
        >
          <ImageWrapper alt={'neXDos GmbH'} src={'/images/logo.png'} className={'h-[24px] my-1'} />
          <span
            className={
              'text-base-text-color font-normal uppercase tracking-[0.15rem] leading-none h-fit pb-[5px] select-none'
            }
          >
            {clientPortalLabel}
          </span>
        </Link>
        <div className={'w-20 flex justify-end pr-4 flex-grow'}>
          <span className={'flex gap-4'}>
            {React.isValidElement(headerFloatRightItems)
              ? React.cloneElement(headerFloatRightItems as any, { hasNotifications: state.hasNotifications })
              : headerFloatRightItems}
          </span>
        </div>
      </div>
    </>
  );
}
